import {
  OwnedToken as AlchemyOwnedToken,
  NftContract,
  NftTokenType,
  OwnedNft as AlchemyOwnedNft,
  TokenMetadataResponse,
} from 'alchemy-sdk'
import {
  ERC20,
  NFTType,
  OwnedERC20,
  NFT,
  OwnedNFT,
} from 'src/common/types/models/Tokens'
import { big, normalize } from 'src/common/utils/big-utils'
import { getKnownToken } from 'src/config/utils/tokens'
import { spamKeywords, spamPatterns } from './spam-tokens'
import { unifyAddress } from 'src/common/utils/web3/unifyAddress'
import { AssetType } from 'src/common/apollo/types/swarmx/graphql'
import { TokenStandard } from 'src/common/apollo/types/swarmx/graphql'
import { isNil } from 'lodash'

export const testIsSpamToken = (name = '', symbol = '') => {
  if (!name || !symbol) return true

  const _name = name.toLowerCase()
  const _symbol = symbol.toLowerCase()

  // Check for keywords
  for (const keyword of spamKeywords) {
    if (_name.includes(keyword) || _symbol.includes(keyword)) {
      return true
    }
  }

  // Check for URL patterns
  for (const pattern of spamPatterns) {
    if (pattern.test(_name) || pattern.test(_symbol)) {
      return true
    }
  }

  // Check for unusual characters at the start
  const unusualStartingChars = /^[!#]/
  if (unusualStartingChars.test(_name) || unusualStartingChars.test(_symbol)) {
    return true
  }

  return false
}

export const normalizeAlchemyOwnedERC20 = (
  alchemyToken: AlchemyOwnedToken,
  chainId: number
): OwnedERC20 => {
  const knownToken = getKnownToken(alchemyToken.contractAddress, chainId)

  if (knownToken) {
    return {
      balance: normalize(
        alchemyToken.rawBalance || '0',
        alchemyToken.decimals || 0
      ),
      ...knownToken,
      logo: alchemyToken.logo || '',
    }
  }

  return {
    id: unifyAddress(alchemyToken.contractAddress),
    contractAddress: alchemyToken.contractAddress,
    name: alchemyToken.name || '',
    symbol: alchemyToken.symbol || '',
    logo: alchemyToken.logo || '',
    balance: normalize(
      alchemyToken.rawBalance || '0',
      alchemyToken.decimals || 0
    ),
    decimals: alchemyToken.decimals || 0,
    type: 'ERC20',
    assetType: AssetType.NoType,
  }
}

export const normalizeAlchemyTokenMetadata = (
  contractAddress: string,
  metadata: TokenMetadataResponse
): ERC20 => {
  return {
    id: unifyAddress(contractAddress),
    contractAddress,
    name: metadata.name || '',
    symbol: metadata.symbol || '',
    logo: metadata.logo || '',
    decimals: metadata.decimals || 0,
    type: 'ERC20',
    assetType: AssetType.NoType,
  }
}

export const normalizeAlchemyNftTokenType = (
  type: NftTokenType
): NFTType | null => {
  if (type === NftTokenType.ERC1155) {
    return 'ERC1155'
  }
  if (type === NftTokenType.ERC721) {
    return 'ERC721'
  }

  return null
}

export const normalizeAlchemyNftContractMetadata = (
  contractAddress: string,
  metadata: NftContract
): NFT | null => {
  const type = normalizeAlchemyNftTokenType(metadata.tokenType)
  if (!type) return null

  return {
    id: unifyAddress(contractAddress),
    contractAddress: contractAddress,
    name: metadata.name || '',
    symbol: metadata.symbol || '',
    type,
    assetType: AssetType.NoType,
    tokenId: 0,
    logo: '',
  }
}

export const normalizeNftForOwner = (
  ownedNft: AlchemyOwnedNft
): OwnedNFT | null => {
  const contractAddress = ownedNft.contract.address
  const type = normalizeAlchemyNftTokenType(ownedNft.tokenType)
  if (!type) return null

  return {
    id: unifyAddress(contractAddress).concat('-').concat(ownedNft.tokenId),
    contractAddress: contractAddress,
    name: ownedNft.contract.name || '',
    symbol: ownedNft.contract.symbol || '',
    logo: ownedNft.image.cachedUrl || '',
    type: type || TokenStandard.NoType,
    tokenId: Number(ownedNft.tokenId),
    kya: ownedNft.tokenUri,
    balance: big(ownedNft.balance),
    rawBalance: ownedNft.balance,
  }
}

export const normalizeAllNftsForOwner = (
  ownedNfts: AlchemyOwnedNft[]
): OwnedNFT[] => {
  return ownedNfts.map(normalizeNftForOwner).filter(i => !isNil(i))
}
